import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ElMessage } from 'element-plus';
import type { ApiResponse } from '../types/models';
import { isUuid, newUuid } from '../utils/idempotency';
import { envelopeCarrier, envelopeCodeMatches, envelopePassed } from './envelopeResult';
// t67：后端 message 上屏前的内部痕迹收口（第二道闸）——原文仍留在 ApiError.message 当取证事实位。
import { sanitizeUserCopy } from './userCopy';

/**
 * 契约口径：《后端项目开发文档 v1.6》§6.0（前后端联调唯一口径）＋ 项负责 seq=2216 的换判法。
 * - ③ 判成败**只看 `resultCode`**：码名 `OK` 才算通过（`envelopeResult.ts` 那份实现），缺载体按不通过并记证据。
 *      `code` 自本批改判只留**取证事实位**（原样塞进 `ApiError.code` 供人看），⛔ 参与任何判定分支——
 *      它是被 `resultCode` 派生的一方，且后端要把它的值翻成整数（HTTP 状态），继续读它就是拿并档值判成败。
 * - ④ HTTP 语义冻结：200 业务失败也走 200（信封判定因此⛔ 不能只看 HTTP 状态）；401 清态；403 静默拦截；409 冲突；410 链接过期；
 *      422 业务规则拒绝；423 账号锁定；429 退避重试；**503 从 5xx 单列**（依赖不可用，不作缺陷报障）。
 * - ⑥ 幂等键 `Idempotency-Key: <uuid>`，值必须是合法 UUID；三态分别处置（缺键/非法键属**前端缺陷**，不得提示用户）。
 * - ⑦ 乐观锁 `rowversion` 读回必带写，`ROWVERSION_CONFLICT` 禁止自动覆盖或静默重试。
 * - 提示文案一律取 `message`，只在需要特殊交互时按 `resultCode` 的码名分支（`envelopeCodeMatches`）。
 */
export const ApiCode = {
  /** §6.0-③ 成功业务码固定字符串 */
  OK: 'OK',
  /* —— 跨切基线（§6.0-④）—— */
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  DATA_SCOPE_DENIED: 'DATA_SCOPE_DENIED',
  END_BINDING_CONFLICT: 'END_BINDING_CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DEPENDENCY_UNAVAILABLE: 'DEPENDENCY_UNAVAILABLE',
  STATE_GUARD_REJECTED: 'STATE_GUARD_REJECTED',
  /* —— 前端必须按 code 分支的专项码 —— */
  /** §6.0-⑦ 乐观锁：数据被他人变更 → 刷新后由用户决定重试 */
  ROWVERSION_CONFLICT: 'ROWVERSION_CONFLICT',
  /** §6.0-⑥ 幂等三态：前两者属前端封装缺陷，不得提示用户 */
  IDEMPOTENCY_KEY_MISSING: 'IDEMPOTENCY_KEY_MISSING',
  IDEMPOTENCY_KEY_INVALID: 'IDEMPOTENCY_KEY_INVALID',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /** 登录失败走 HTTP 200，刻意不触发全局 401 —— 登录页须自行按 code 显示 */
  AUTH_LOGIN_FAILED: 'AUTH_LOGIN_FAILED',
  /** 423：5 次失败锁 15min —— 禁用登录按钮，继续重试只会延长锁定 */
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_CAPTCHA_INVALID: 'AUTH_CAPTCHA_INVALID',
  AUTH_SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  /** 403：跨端登录隔离——账号归属另一个控制台，本端不签发会话（m02355）。 */
  AUTH_END_NOT_ALLOWED: 'AUTH_END_NOT_ALLOWED',
  /** 410：导出链接过期 —— 不重试原链接，引导重走审批 */
  EXPORT_LINK_EXPIRED: 'EXPORT_LINK_EXPIRED',
  EXPORT_APPROVAL_REQUIRED: 'EXPORT_APPROVAL_REQUIRED',
  /**
   * S-10（seq372 §二）：这里**没有**批量条目级守卫码。后端 `ba9cfb7` 已删 `BATCH_ITEM_STATUS_GUARD`，
   * `items[].code` 从来就是该条守卫真实返回的具体码（`ORDER_NOT_FOUND` /
   * `ORDER_STATUS_ILLEGAL_TRANSITION` / `URGE_ORDER_NOT_APPLICABLE` …）。不为不存在的码留常量与分支。
   */
  ORDER_DELIVERY_METHOD_UNSUPPORTED_P0: 'ORDER_DELIVERY_METHOD_UNSUPPORTED_P0',
  /**
   * seq322 §七 D-06：`assign`/`reassign`/`pool/assign` 的 `courierId` **有值但库内无此交付员**
   * → 422 本码（业务不可执行，要提示用户换人）；**缺失/空/非正数** → 400 `VALIDATION_FAILED`
   * （请求体不合法，前端应在提交前拦截、不该发出请求）。两码不得合并处置。
   */
  COURIER_NOT_FOUND: 'COURIER_NOT_FOUND',
} as const;

/** HTTP 层语义（§6.0-④），与业务码分开，避免再次混用 int 当业务码 */
const Http = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  /** §6.0-④ 新增：导出链接已过期 */
  GONE: 410,
  /** §6.0-④ 新增：账号已锁定 */
  LOCKED: 423,
  GUARD_REJECTED: 422,
  TOO_MANY: 429,
  /** §6.0-④：从 5xx 单列，提示「稍后重试」而非报障 */
  DEPENDENCY_UNAVAILABLE: 503,
} as const;

/**
 * 契约漂移取证：实测形状与冻结口径不一致时**记一条样本证据**（不静默容忍，也⛔ 拿它当判定依据），
 * 供 `getContractDrift()` 导出后回报后端。
 *
 * ⚠ 本文件**曾**为旧部署包留过两枚兜底（`code` 是 int 0、缺 `code` 只看 `isSuccess` 布尔），
 * seq=2216 换判法时一并撤掉：那两枚都是「拿被派生的那一枚判成败」，留着就等于 `code` 从没退场。
 * 缺载体现在只记证据、只判不通过。旧部署包／回退那一格由接桩与 `httpStatus` 那组语义承担（§6.0-④）。
 *
 * 导出给 API 适配层复用：端点已冻结但**字段名未冻结**（如看板族）时，适配一次并在此记账。
 */
const driftSamples = new Map<string, { count: number; sample: string }>();

export function recordDrift(kind: string, observed: unknown): void {
  const hit = driftSamples.get(kind);
  if (hit) {
    hit.count += 1;
    return;
  }
  driftSamples.set(kind, { count: 1, sample: JSON.stringify(observed) });
  console.warn(`[contract-drift] ${kind} 与 §6.0 冻结口径不一致，实测：${JSON.stringify(observed)}`);
}

export function getContractDrift(): Array<{ kind: string; count: number; sample: string }> {
  return Array.from(driftSamples.entries()).map(([kind, v]) => ({ kind, ...v }));
}

export class ApiError extends Error {
  /**
   * **取证事实位**（seq=2216）：后端信封里那枚 `code` 原样带回来，只准拿去**显示与报障**。
   * ⛔ 参与判定、⛔ 拿去比码名或比 int——它的值要翻成 HTTP 状态（多枚码并成一档），
   * 判定一旦读它，翻整数当天就整片失配。需要码名一律读 `businessCode`。
   */
  readonly code: string | number;
  /**
   * 信封里的业务码唯一载体（PRD `:55`）。`:69` 之后它是**定因的唯一入口**，seq=2216 之后**成败也只看它**：
   * 本类的存在意义就是**别把它丢掉**——这里不接，`envelopePassed` 读不到载体就判不通过、
   * `readErrorShapeOf` 读不到它就**不定因**（渲染走 `NO_CARRIER_COPY` 那句定版文案），
   * 而盘上缺值那一刻是真缺（09-18 那趟之前部署版没有这枚），⛔ 回读隔壁那枚 `code` 顶上去。
   */
  readonly resultCode?: string | number;
  readonly httpStatus?: number;
  readonly traceId?: string;
  readonly payload?: unknown;

  constructor(
    message: string,
    code: string | number,
    httpStatus?: number,
    traceId?: string,
    payload?: unknown,
    resultCode?: string | number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.resultCode = resultCode;
    this.httpStatus = httpStatus;
    this.traceId = traceId;
    this.payload = payload;
  }

  /**
   * 这枚错误的业务码名，**只从 `resultCode` 取**；缺载体就是缺值（⛔ `'UNKNOWN'` 之类的哨兵顶上，
   * 同 `readState.ts` 那条）。视图要显示「后端给的因」一律读这里，别再读 `err.code`。
   */
  get businessCode(): string | undefined {
    return envelopeCarrier(this);
  }

  /**
   * 码名分支的唯一落点：比 `businessCode`（＝`resultCode` 那枚值）。
   * 后端把 `code` 翻成整数之后，这组 getter 与 `code` 无关、零改动（seq=2216 换判法要的就是这一格）；
   * 代价是载体缺席那一段时间窗内它们一律为假 ⇒ 只剩 `httpStatus` 那组语义定档，⛔ 拿「码名读不出」当「通过」。
   */
  private is(code: string): boolean {
    return envelopeCodeMatches(this, code);
  }

  /** §6.0-⑦ 乐观锁冲突：与幂等/抢单/已派单同处 409，但文案与处置不同，必须按载体码名分支 */
  get isRowversionConflict(): boolean {
    return this.is(ApiCode.ROWVERSION_CONFLICT);
  }

  /** seq322 §七 D-06 的 422 分支：所选交付员库内不存在 → 提示换人，不是表单校验缺陷 */
  get isCourierNotFound(): boolean {
    return this.is(ApiCode.COURIER_NOT_FOUND);
  }

  /**
   * §6.0-⑥ + seq322 §五 裁定（v1.8 权威口径）：**同键 + 同 payload → 200 `OK` + 原结果**（含原
   * `orderNo`），是弱网重试/重复点击的透明重放，走成功分支、无需二次查询；
   * **同键 + 不同 payload → 409 `IDEMPOTENCY_KEY_REUSED`** 才是这里命中的分支 —— 键空间污染，
   * 必须显式拒绝。前端**绝不可**为此自动换新键重发：那正是绕过幂等、直接造出重复订单的路径。
   */
  get isIdempotencyReused(): boolean {
    return this.is(ApiCode.IDEMPOTENCY_KEY_REUSED);
  }

  /** §6.0-⑥ 缺键 / 键非 UUID：属**前端封装缺陷**，不得提示用户，只记证据 + console.error */
  get isIdempotencyFrontendDefect(): boolean {
    return this.is(ApiCode.IDEMPOTENCY_KEY_MISSING) || this.is(ApiCode.IDEMPOTENCY_KEY_INVALID);
  }

  /** 409 族（乐观锁 / 幂等 / 抢单竞争 / 已派单 / 配置版本冲突）：一律不自动覆盖 */
  get isConflict(): boolean {
    return this.httpStatus === Http.CONFLICT;
  }

  get isUnauthorized(): boolean {
    return this.httpStatus === Http.UNAUTHORIZED || this.is(ApiCode.UNAUTHENTICATED) || this.is(ApiCode.TOKEN_EXPIRED);
  }

  /** §6.0-④ 403：已鉴权无权限（数据范围越权、端绑定互斥、导出未审批、审计不可改）——静默拦截、不重试 */
  get isForbidden(): boolean {
    return (
      this.httpStatus === Http.FORBIDDEN ||
      this.is(ApiCode.FORBIDDEN) ||
      this.is(ApiCode.DATA_SCOPE_DENIED) ||
      this.is(ApiCode.END_BINDING_CONFLICT) ||
      this.is(ApiCode.EXPORT_APPROVAL_REQUIRED)
    );
  }

  /** §6.0-④ 422：状态机守卫 / 业务规则拒绝（十余个码共用一档，文案取 message） */
  get isGuardRejected(): boolean {
    return this.httpStatus === Http.GUARD_REJECTED;
  }

  /** 423 账号锁定：禁用登录按钮，继续重试只会延长锁定 */
  get isAccountLocked(): boolean {
    return this.httpStatus === Http.LOCKED || this.is(ApiCode.AUTH_ACCOUNT_LOCKED);
  }

  /** 410 导出链接过期：不重试原链接 */
  get isExportLinkExpired(): boolean {
    return this.httpStatus === Http.GONE || this.is(ApiCode.EXPORT_LINK_EXPIRED);
  }

  /** 503 依赖不可用：从 5xx 单列，不作缺陷报障 */
  get isDependencyUnavailable(): boolean {
    return this.httpStatus === Http.DEPENDENCY_UNAVAILABLE || this.is(ApiCode.DEPENDENCY_UNAVAILABLE);
  }

  /** 端点未部署（HTTP 404/405）——区别于「资源不存在」，供错误归类与取证判定 */
  get isEndpointMissing(): boolean {
    return this.httpStatus === Http.NOT_FOUND || this.httpStatus === Http.METHOD_NOT_ALLOWED;
  }

  /**
   * 面向用户的文案：冻结口径要求 5xx 携带 traceId 报障。
   * t67：message 是后端信封原文（取证事实位，⛔ 不改写）；上屏前经 sanitizeUserCopy 判一次——
   * 内部腔调（物理表名／内部主键／英文异常原文／裸业务码）换中性中文兜底，好文案逐字透传。
   */
  get displayMessage(): string {
    const base = sanitizeUserCopy(this.message);
    const withTrace = (text: string): string => (this.traceId ? `${text}（traceId: ${this.traceId}）` : text);
    if (this.isRowversionConflict) return withTrace(`${base}｜数据已被他人变更，请刷新后重试`);
    if (this.isIdempotencyReused) return withTrace('该提交键已被另一笔内容占用，本次未被执行；请返回上一步核对内容后重新发起，不要更换新键重录（会产生重复单据）');
    if (this.isAccountLocked) return withTrace(`${base}｜锁定期间继续尝试只会延长时间`);
    if (this.isExportLinkExpired) return withTrace('下载链接已过期，请重新发起导出审批');
    if (this.isDependencyUnavailable) return withTrace('服务依赖暂不可用，请稍后重试');
    return withTrace(base);
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** 写操作幂等键，由请求头 Idempotency-Key 承载（§6.0-⑥） */
    idempotencyKey?: string;
    /** 跳过鉴权头（登录/验证码） */
    skipAuth?: boolean;
    /**
     * t28（R8）**按请求**声明端标识 `X-Client-End`：只给交付管理端登录请求用。后端登录层端准入闸的
     * 判据是「本头严格等于 DELIVERY_ADMIN 且账号是超管 ⇒ 403 AUTH_END_NOT_ALLOWED（令牌签发之前）」，
     * 而本端 `VITE_APP_END` 故意是 ADMIN（使用者角色的 endBinding 是 [ADMIN]，见 .env.development）
     * ⇒ 登录请求必须自己声明端标识。缺省不传＝仍是 `END`；⛔ 不许拿它改整端默认值——那会牵动端绑定
     * 判定与菜单/路由，超出本任务范围。
     */
    clientEnd?: 'DELIVERY_ADMIN';
    /** 网络错误/5xx/429 自动重试次数（**仅只读方法生效**；写操作受 S-13 禁令一律不自动重试） */
    retry?: number;
    /** 静默模式：不弹全局错误提示，由调用方处理 */
    silent?: boolean;
    /**
     * 403 也对用户可见（默认 403 静默拦截）。
     *
     * 默认策略不变：403 一律静默——数据范围越权、端绑定互斥、导出未审批这些口各自有就地反馈。
     * 但**业务拒绝也可能以 403 下发**：交付管理端的改派口在「配送员工号缺失」一类业务前置不满足时
     * 返回 403 `GRAB_CARRIER_JOB_NO_MISSING`（实服务实测），若继续静默，用户看到的只是弹窗不关、
     * 订单没变、屏幕上一个字都没有。故只给**显式声明**该开关的调用点（当前仅 reassignOrder）放行到
     * 统一提示；⛔ 不许改成全局打开，也不许把业务拒绝渲染成成功语气。
     */
    surfaceForbidden?: boolean;
  }
}

export interface TokenProvider {
  getAccessToken(): string | null;
  onUnauthorized(): void;
}

let tokenProvider: TokenProvider = {
  getAccessToken: () => null,
  onUnauthorized: () => undefined,
};

export function bindTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

const END = import.meta.env.VITE_APP_END || 'ADMIN';

const READONLY_METHODS = new Set(['get', 'head', 'options']);

/**
 * S-13 落地前的写操作重试禁令（seq340）：服务端对 `Idempotency-Key` 当前只做
 * 「存在性 + UUID 格式」校验，**既无登记表比对也无结果缓存** ⇒ 「带同一个键重放是安全的」
 * 这个前提为假，网络抖动重试 / 超时重发会真实改坏数据。所以只读方法保留退避重试，
 * 写操作一律不自动重试；网络抖动、超时、401 刷新后重放三条自动路径全部关闭。
 * 用户**手动**再点一次按 seq322 §五 C-05 第一行走「同键重放」语义（`withIdempotency` 按业务
 * 意图复用同一 UUID，不换键），但同键重放要拿回原结果仍取决于 S-13 的登记表，故视图层另加
 * 「一次业务意图只允许一条在途写」的单飞保护。
 */
function shouldRetry(config: InternalAxiosRequestConfig & { __retryCount?: number }): boolean {
  const method = (config.method || 'get').toLowerCase();
  if (!READONLY_METHODS.has(method)) {
    if (config.retry) {
      recordDrift('S-13 未落地前写操作不得自动重试（调用方显式请求的重试已就地禁用）', `${method.toUpperCase()} ${config.url ?? ''}`);
    }
    return false;
  }
  const max = config.retry ?? (method === 'get' ? 2 : 0);
  const done = config.__retryCount ?? 0;
  return done < max;
}

function backoff(attempt: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 220 * (attempt + 1)));
}

/**
 * 统一的用户侧错误处置策略（§6.0-④）。
 * HTTP 非 2xx 与「HTTP 200 但信封判不通过（判据只看 `resultCode`）」两条路必须共用同一个入口：axios 的 fulfilled
 * 分支内 throw 不会被同一拦截器的 rejected 分支捕获，若只在 rejected 分支提示，
 * 状态守卫、导出链接过期、依赖不可用等 200 业务失败会全部静默——用户看不到任何反馈。
 */
function surfaceError(apiError: ApiError, config?: InternalAxiosRequestConfig): void {
  if (apiError.isUnauthorized) {
    tokenProvider.onUnauthorized();
  }

  if (apiError.isIdempotencyFrontendDefect) {
    // §6.0-⑥：缺键 / 键非法属前端自身缺陷，**不得提示用户**；保留开发侧证据便于定位调用点
    console.error(
      `[idempotency-defect] ${apiError.businessCode ?? '缺载体'} ${config?.method?.toUpperCase() ?? ''} ${config?.url ?? ''} traceId=${apiError.traceId ?? '-'}`,
    );
    return;
  }

  // silent 由调用方自渲染（如登录页需按码给文案）；403 默认静默拦截不重试；404/405 端点缺失交由接桩处理。
  // 唯一例外：调用点显式声明 surfaceForbidden（改派口把业务拒绝也走 403 下发）——此时按普通错误提示，
  // 仍不改 403 的全局语义（不重试、不跳转、不清会话）。
  const forbiddenHidden = apiError.isForbidden && !config?.surfaceForbidden;
  if (config?.silent || forbiddenHidden || apiError.isEndpointMissing) return;

  // §七 D-06 的 422 分支：契约要求「告知用户换人」，通用文案无法承载这个动作，
  // 故全局静默，由分配弹窗就地清空人选并给出可操作提示（与 400 的行内红框分路）。
  if (apiError.isCourierNotFound) return;

  ElMessage({
    type: apiError.isConflict || apiError.isGuardRejected ? 'warning' : 'error',
    message: apiError.displayMessage,
  });
}

export interface HttpClientOptions {
  adapter?: AxiosRequestConfig['adapter'];
  /** §6.0-①：base 即后端根（`http://<backend-host>:<port>` 或同源 ''），**不得拼 /api** */
  baseURL?: string;
}

/**
 * §6.0-③ 序列化约定实测补充：后端主键为 `long`，线上是 JSON **数字**，而前端模型统一以
 * **字符串**承载 id（见 `utils/contract.ts`）。不在出口归一，就会出现
 * 「路由参数 `'12'` === 响应里的 `12`」恒假的静默失配（详情跳转会白屏一类的根因）。
 * 只处理 `id` / `*Id` / `*Ids` 三种命名，其它数值（fee、counts、permVersion…）原样保留。
 */
function isIdKey(key: string): boolean {
  return key === 'id' || key.endsWith('Id') || key.endsWith('Ids');
}

export function stringifyWireIds<T>(value: T, depth = 0): T {
  if (depth > 12 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => stringifyWireIds(item, depth + 1)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!isIdKey(key)) {
      out[key] = stringifyWireIds(raw, depth + 1);
      continue;
    }
    // 超出安全整数的 long 在 JSON.parse 阶段已丢精度，这里只做类型归一，不假装能恢复
    out[key] = Array.isArray(raw)
      ? raw.map((item) => (typeof item === 'number' ? String(item) : stringifyWireIds(item, depth + 1)))
      : typeof raw === 'number'
        ? String(raw)
        : stringifyWireIds(raw, depth + 1);
  }
  return out as unknown as T;
}

export function createHttpClient(options: HttpClientOptions = {}): AxiosInstance {
  const baseURL = options.baseURL ?? import.meta.env.VITE_API_BASE_URL ?? '';

  const client = axios.create({
    baseURL,
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
    adapter: options.adapter,
    // COORD-RULINGS §I 终态：鉴权只走 `Authorization: Bearer`、不依赖 Cookie，凭证模式与
    // 令牌头架构本就互斥，故显式写死 false（防后来者为联调顺手打开）。§I 已撤回
    // 「ACAO:* + credentials 会被浏览器拒」那条推断，本值不以任何 CORS 报错为依据——
    // **前端不为 CORS 做任何改动**，跨域归部署侧与 dev 代理负责。
    withCredentials: false,
  });

  client.interceptors.request.use((config) => {
    const token = tokenProvider.getAccessToken();
    if (token && !config.skipAuth) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    // t28：`END` 仍是**无条件默认值**（本端 = VITE_APP_END = ADMIN）；只有显式带 `clientEnd` 的
    // 请求（登录请求，见 api/auth.ts）才发 DELIVERY_ADMIN —— 后端登录层端准入闸据此把超管拒在
    // 令牌签发之前。⛔ 其余请求一个字节不变。
    config.headers.set('X-Client-End', config.clientEnd ?? END);

    // §6.0-⑥：写操作一律携带合法 UUID 幂等键。缺键 → 409 MISSING、非 UUID → 400 INVALID，
    // 两者都被契约判定为**前端封装缺陷**（不得提示用户），故在出口兜底生成/纠正；
    // 调用方传入的非法键不静默吞掉——记一条漂移证据，便于回查修调用点。
    const method = (config.method || 'get').toLowerCase();
    const isWrite = method !== 'get' && method !== 'head' && method !== 'options';
    if (isWrite) {
      const provided = config.idempotencyKey;
      if (provided && !isUuid(provided)) {
        recordDrift('调用方传入的 Idempotency-Key 非 UUID（已就地纠正，请修调用点）', provided);
      }
      const key = provided && isUuid(provided) ? provided : newUuid();
      config.idempotencyKey = key;
      config.headers.set('Idempotency-Key', key);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse<unknown> | undefined;
      // 非统一封装（文件流、后端裸 404 页）直接透传。⚠ 这里量的是**键在不在**（形状），不是键的值——
      // 判成败⛔ 读 `code`（seq=2216），故两枚键任一枚在册就算「像是统一封装」，`code` 真被摘掉那天也不会误判成裸流。
      if (!body || typeof body !== 'object' || (!('code' in body) && !('resultCode' in body))) {
        if (response.status >= Http.BAD_REQUEST) {
          throw new ApiError(`接口未返回统一响应封装（HTTP ${response.status}）`, response.status, response.status);
        }
        recordDrift('响应缺少 {code,message,data,traceId} 封装', { status: response.status });
        return response;
      }
      // 实测：部分 §6.2 端点连 `traceId` 键都不写（不只是 null），一律记漂移
      if (body.traceId === null || body.traceId === undefined || body.traceId === '') {
        recordDrift('traceId 未透传（§6.0-③ 要求全链路，前端无法据此报障）', { present: 'traceId' in body });
      }
      if (!envelopePassed(body, recordDrift)) {
        const apiError = new ApiError(
          // t67：⛔ 不再在构造点写第二枚兜底字面量；空 message 交 sanitizeUserCopy 统一兜底（单一口径）。
          body.message ?? '',
          body.code,
          response.status,
          body.traceId ?? undefined,
          body.data,
          body.resultCode,
        );
        surfaceError(apiError, response.config);
        throw apiError;
      }
      response.data = stringifyWireIds(body.data);
      return response;
    },
    async (error: AxiosError<ApiResponse<unknown>>) => {
      const config = error.config as (InternalAxiosRequestConfig & { __retryCount?: number }) | undefined;
      const status = error.response?.status;
      const body = error.response?.data;

      // §6.0-④：429 与网络错误/5xx 走退避重试——S-13 落地前**只对只读方法生效**，见 shouldRetry
      const retriable = !error.response || status === Http.TOO_MANY || (status ?? 0) >= 500;
      if (config && retriable && shouldRetry(config)) {
        config.__retryCount = (config.__retryCount ?? 0) + 1;
        await backoff(config.__retryCount - 1);
        return client.request(config);
      }

      const apiError = new ApiError(
        body?.message || error.message || '网络异常，请稍后重试',
        body?.code ?? status ?? ApiCode.INTERNAL_ERROR,
        status,
        body?.traceId ?? undefined,
        body?.data,
        body?.resultCode,
      );

      surfaceError(apiError, config);
      return Promise.reject(apiError);
    },
  );

  return client;
}

/** §6.0-⑤：分页 size 默认 20、上限 200 */
export const PAGE_SIZE_MAX = 200;
export const PAGE_SIZE_DEFAULT = 20;

export function clampPageSize(size?: number): number {
  const n = Math.floor(Number(size ?? PAGE_SIZE_DEFAULT));
  if (!Number.isFinite(n) || n < 1) return PAGE_SIZE_DEFAULT;
  return Math.min(n, PAGE_SIZE_MAX);
}

/** §6.0-⑤ 排序子句：`{ field, dir }`，多组按数组顺序传递 */
export interface SortClause {
  field: string;
  dir: 'asc' | 'desc';
}

/**
 * §6.0-⑤：排序参数 `sort=field,asc|desc`，**可多组、逗号分隔**（v1.6 已冻结分隔符），
 * 即 `sort=createdAt,desc,orderNo,asc`。不得使用重复 sort 参数，也不得自创其它分隔符。
 */
export function buildSort(clauses: SortClause | SortClause[]): string | undefined {
  const list = (Array.isArray(clauses) ? clauses : [clauses]).filter((item) => item?.field);
  if (list.length === 0) return undefined;
  return list.map((item) => `${item.field},${item.dir === 'asc' ? 'asc' : 'desc'}`).join(',');
}

/** 统一取数：返回 ApiResponse.data（响应拦截器已解封装） */
export async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  const res = await promise;
  return res.data;
}
